import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';

export type AccountCreationRoleKind = 'verifier' | 'approver';

export type AccountCreationRoleRow = {
  id: string;
  email: string;
  role: AccountCreationRoleKind;
  created_at: string;
  created_by: string | null;
};

const TABLE = 'account_creation_roles';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export async function fetchAccountCreationRoles(): Promise<AccountCreationRoleRow[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from(TABLE)
    .select('id,email,role,created_at,created_by')
    .order('role', { ascending: true })
    .order('email', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => ({
    id: String(row.id),
    email: normalizeEmail(String(row.email ?? '')),
    role: String(row.role) as AccountCreationRoleKind,
    created_at: String(row.created_at ?? ''),
    created_by: row.created_by == null ? null : String(row.created_by),
  }));
}

export async function addAccountCreationRole(
  email: string,
  role: AccountCreationRoleKind,
  createdBy?: string | null,
): Promise<void> {
  const normalized = normalizeEmail(email);
  if (!normalized || !normalized.includes('@')) {
    throw new Error('Enter a valid email address.');
  }
  const db = getDbSupabase('forms');
  const { error } = await db.from(TABLE).upsert(
    {
      email: normalized,
      role,
      created_by: createdBy?.trim() || null,
    },
    { onConflict: 'email,role' },
  );
  if (error) throw new Error(formatSupabaseError(error));
}

export async function setAccountCreationRoleEnabled(
  email: string,
  role: AccountCreationRoleKind,
  enabled: boolean,
  createdBy?: string | null,
): Promise<void> {
  if (enabled) {
    await addAccountCreationRole(email, role, createdBy);
    return;
  }
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  const db = getDbSupabase('forms');
  const { error } = await db.from(TABLE).delete().eq('email', normalized).eq('role', role);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchMyAccountCreationCapabilities(): Promise<{
  canSubmit: boolean;
  canVerify: boolean;
  canApprove: boolean;
}> {
  const db = getDbSupabase('forms');
  const [{ data: submitData }, { data: verifyData }, { data: approveData }] = await Promise.all([
    db.rpc('af_can_submit_internal', { p_form_slug: 'account-creation' }),
    db.rpc('af_can_verify_form', { p_form_slug: 'account-creation' }),
    db.rpc('af_can_approve_form', { p_form_slug: 'account-creation' }),
  ]);
  return {
    canSubmit: Boolean(submitData),
    canVerify: Boolean(verifyData),
    canApprove: Boolean(approveData),
  };
}
